import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import type { Task, TaskSummary } from "@/types/task";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckSquare2,
  Clock,
  ArrowRight,
  PhoneCall,
  CalendarCheck2,
  CalendarClock,
  Mail,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/context/ToastContext";

export function DashboardTasksWidget() {
  const { success, error: toastError } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const start = new Date(now.setHours(0, 0, 0, 0)).toISOString();
      const end = new Date(now.setHours(23, 59, 59, 999)).toISOString();

      const [listRes, sumRes] = await Promise.all([
        api.get(
          `/api/Task/List?view=today&scope=mine&from=${encodeURIComponent(
            start
          )}&to=${encodeURIComponent(end)}&limit=5`
        ),
        api.get(
          `/api/Task/Summary?scope=mine&from=${encodeURIComponent(
            start
          )}&to=${encodeURIComponent(end)}`
        ),
      ]);

      setTasks(unwrapPage<Task>(listRes).rows);
      setSummary(unwrapObject<TaskSummary>(sumRes));
    } catch {
      // Fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleQuickComplete = async (task: Task) => {
    try {
      await api.patch(`/api/Task/Complete/${task.id}`, {
        outcome: "Completed from dashboard widget",
      });
      success("Task Completed", `"${task.title}" marked as done.`);
      fetchTasks();
    } catch (err: any) {
      toastError("Error", err.message || "Failed to complete task.");
    }
  };

  const getTaskIcon = (type: string) => {
    switch (type) {
      case "CALL":
        return <PhoneCall className="size-3.5 text-blue-600 dark:text-blue-400" />;
      case "MEETING":
        return <CalendarClock className="size-3.5 text-purple-600 dark:text-purple-400" />;
      case "EMAIL":
        return <Mail className="size-3.5 text-sky-600 dark:text-sky-400" />;
      default:
        return <CalendarCheck2 className="size-3.5 text-indigo-600 dark:text-indigo-400" />;
    }
  };

  return (
    <Card className="shadow-xs border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2.5">
          <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <CheckSquare2 className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">Today's Agenda & Follow-ups</CardTitle>
              {summary && summary.overdue > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/60 text-red-700 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="size-2.5" />
                  {summary.overdue} overdue
                </span>
              )}
            </div>
            <CardDescription>
              {summary?.today
                ? `You have ${summary.today} action item${summary.today > 1 ? "s" : ""} scheduled for today`
                : "No pending tasks scheduled for today"}
            </CardDescription>
          </div>
        </div>

        <Link to="/tasks">
          <Button variant="ghost" size="sm" className="text-xs text-primary gap-1">
            Open My Day <ArrowRight className="size-3.5" />
          </Button>
        </Link>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="space-y-2 py-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground border border-dashed border-border rounded-xl bg-card/40">
            <CheckCircle2 className="size-7 mx-auto mb-1.5 opacity-40 text-emerald-600 dark:text-emerald-400" />
            <p className="text-xs font-medium">All caught up for today!</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Review upcoming follow-ups or create a new task.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
            {tasks.map((t) => {
              const timeStr = new Date(t.due_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
              const isDone = t.task_status === "DONE";

              return (
                <div
                  key={t.id}
                  className="p-3 flex items-center justify-between gap-3 hover:bg-muted/20 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => handleQuickComplete(t)}
                      className="size-4 rounded border border-muted-foreground/40 hover:border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-transparent flex items-center justify-center shrink-0 transition-colors"
                      title="Quick complete"
                    >
                      <CheckCircle2 className="size-3 fill-current" />
                    </button>

                    <div className="p-1 rounded bg-muted/60 shrink-0">
                      {getTaskIcon(t.task_type)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {t.title}
                      </p>
                      {t.related_name && (
                        <p className="text-[11px] text-muted-foreground truncate">
                          {t.related_type}: {t.related_name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] font-semibold text-muted-foreground bg-muted/50 px-2 py-0.5 rounded flex items-center gap-1">
                      <Clock className="size-3" />
                      {timeStr}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
